/**
 * FDBookingReview
 * ─────────────────────────────────────────────────────────────
 * Front Desk Review panel (demo mode). Shows approval-required
 * bookings and Smart Holds (abandoned mid-form sessions) so
 * front-desk staff can triage directly from FD mode.
 *
 * Demo note: Front Desk runs with no auth token, so rather than
 * call the authenticated `/api/echo/booking-widget/pending-reviews`
 * endpoint we seed three realistic mock rows and wire Approve /
 * Reject / Recover to local state + toasts. In real mode the FD
 * would swap these fetchers for the authenticated versions.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import {
  ClipboardList, Sparkles, Check, X, ChevronDown, ChevronUp, Clock,
  UserPlus, UserCheck, Stethoscope, HeartPulse, ShieldCheck,
} from 'lucide-react';
import { C } from './frontDeskData';
import { useAppMode } from '../../../contexts/AppModeContext';

/* Seed mock rows — tuned to feel like what a front desk would
   actually see: 2 approvals pending, 1 smart hold. */
const SEED_REVIEWS = [
  {
    request_id: 'req_m7a2x',
    patient: { first_name: 'Ava',    last_name: 'Martinez', phone: '(512) 555-0134' },
    visit_type_name: 'Sick Visit',
    slot: { label: 'Today · 5:15 PM' },
    provider_pref: 'Dr. Chen',
    lens_captured: true,
    received_at: new Date(Date.now() - 9 * 60 * 1000).toISOString(),
    hold_until: new Date(Date.now() + 6 * 60 * 1000).toISOString(),
  },
  {
    request_id: 'req_p3k8q',
    patient: { first_name: 'Derek', last_name: 'Nguyen',   phone: '(512) 555-0217' },
    visit_type_name: 'New Patient',
    slot: { label: 'Tomorrow · 9:45 AM' },
    provider_pref: 'Dr. Patel',
    lens_captured: false,
    received_at: new Date(Date.now() - 21 * 60 * 1000).toISOString(),
    hold_until: new Date(Date.now() + 2 * 60 * 1000).toISOString(),
  },
];

const SEED_HOLDS = [
  {
    session_id: 'sess_zx91',
    visit_type_name: 'Wellness / Annual',
    slot: { label: 'Tomorrow · 10:30 AM' },
    provider: 'Dr. Chen',
    created_at: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
  },
];

const VisitIcon = ({ name = '' }) => {
  const { isDemo } = useAppMode();
  const n = name.toLowerCase();
  if (/new/.test(n))          return <UserPlus     style={{ width: 11, height: 11, color: '#555' }} />;
  if (/sick/.test(n))         return <HeartPulse   style={{ width: 11, height: 11, color: '#555' }} />;
  if (/wellness|annual/.test(n)) return <ShieldCheck style={{ width: 11, height: 11, color: '#555' }} />;
  return <Stethoscope style={{ width: 11, height: 11, color: '#555' }} />;
};

const fmtWait = (iso) => {
  if (!iso) return '';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 min ago';
  return `${mins} min ago`;
};

export const FDBookingReview = () => {
  const [expanded, setExpanded] = useState(true);
  const [reviews, setReviews] = useState(SEED_REVIEWS);
  const [holds, setHolds] = useState(SEED_HOLDS);
  const [actingOn, setActingOn] = useState(null);

  const decide = (id, action) => {
    setActingOn(id);
    setTimeout(() => {
      const row = reviews.find((r) => r.request_id === id);
      setReviews((prev) => prev.filter((r) => r.request_id !== id));
      setActingOn(null);
      if (action === 'approve' && row) {
        const firstName = row.patient?.first_name || '';
        const slotLabel = row.slot?.label || 'your requested time';
        const visit = row.visit_type_name || 'visit';
        const provider = row.provider_pref ? ` with ${row.provider_pref}` : '';
        const smsText = `Hi ${firstName}, your ${visit}${provider} is confirmed for ${slotLabel}. Reply STOP to opt out. — Echo`;
        toast.success('Approved · Echo AI agent texted the patient', {
          description: `"${smsText}"`,
        });
      } else {
        toast.success('Booking rejected · hold released');
      }
    }, 300);
  };

  const recover = (sid) => {
    setActingOn(sid);
    setTimeout(() => {
      setHolds((prev) => prev.filter((h) => h.session_id !== sid));
      setActingOn(null);
      toast.success('Finish-booking SMS queued', { description: 'Recovery campaign logged.' });
    }, 300);
  };

  const totalCount = reviews.length + holds.length;
  if (totalCount === 0) return null;

  return (
    <div
      style={{ margin: '12px 16px 0', borderRadius: 12, border: '1px solid #ebebeb', background: '#fff', overflow: 'hidden' }}
      data-testid="fd-booking-review"
    >
      <button
        onClick={() => setExpanded((p) => !p)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer' }}
        data-testid="fd-booking-review-toggle"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ClipboardList style={{ width: 13, height: 13, color: C.black }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: C.black }}>Bookings to review</span>
          {reviews.length > 0 && (
            <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 10, background: C.black, color: '#fff', fontWeight: 600 }}>
              {reviews.length} pending
            </span>
          )}
          {holds.length > 0 && (
            <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 10, background: C.amber, color: '#fff', fontWeight: 600 }}>
              {holds.length} smart hold{holds.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {expanded
          ? <ChevronUp style={{ width: 14, height: 14, color: '#aaa' }} />
          : <ChevronDown style={{ width: 14, height: 14, color: '#aaa' }} />}
      </button>

      {expanded && (
        <div style={{ borderTop: '1px solid #f0f0f0' }}>
          {/* Smart Holds */}
          {holds.map((h) => (
            <div
              key={h.session_id}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid #f8f8f8', background: '#fffaed' }}
              data-testid={`fd-smart-hold-${h.session_id}`}
            >
              <Sparkles style={{ width: 11, height: 11, color: C.amber, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: C.black }}>Smart hold</span>
                <span style={{ fontSize: 10, color: '#888', marginLeft: 6 }}>
                  {h.visit_type_name} · {h.slot?.label}{h.provider ? ` · ${h.provider}` : ''}
                </span>
                <span style={{ fontSize: 9, color: '#aaa', marginLeft: 6 }}>· dropped {fmtWait(h.created_at)}</span>
              </div>
              <button
                onClick={() => recover(h.session_id)}
                disabled={actingOn === h.session_id}
                style={{ fontSize: 9, fontWeight: 600, padding: '3px 10px', borderRadius: 5, background: '#8a5a00', color: '#fff', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', opacity: actingOn === h.session_id ? 0.5 : 1 }}
                data-testid={`fd-smart-hold-recover-${h.session_id}`}
              >
                Send finish-booking SMS
              </button>
            </div>
          ))}

          {/* Pending approvals */}
          {reviews.map((r) => (
            <div
              key={r.request_id}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid #f8f8f8' }}
              data-testid={`fd-review-row-${r.request_id}`}
            >
              <VisitIcon name={r.visit_type_name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: C.black }}>
                  {r.patient.first_name} {r.patient.last_name}
                </span>
                <span style={{ fontSize: 10, color: '#888', marginLeft: 6 }}>
                  {r.visit_type_name} · {r.slot?.label} · {r.provider_pref}
                </span>
                {r.lens_captured && (
                  <span style={{ fontSize: 9, color: C.green, marginLeft: 6, fontWeight: 500 }}>
                    <UserCheck style={{ width: 9, height: 9, display: 'inline', marginRight: 2 }} />Lens OK
                  </span>
                )}
                <span style={{ fontSize: 9, color: '#aaa', marginLeft: 6 }}>
                  <Clock style={{ width: 9, height: 9, display: 'inline', marginRight: 1 }} />{fmtWait(r.received_at)}
                </span>
              </div>
              <button
                onClick={() => decide(r.request_id, 'reject')}
                disabled={actingOn === r.request_id}
                style={{ fontSize: 9, fontWeight: 600, padding: '3px 8px', borderRadius: 5, background: '#fff', color: '#A32D2D', border: '1px solid #fecaca', cursor: 'pointer', whiteSpace: 'nowrap' }}
                data-testid={`fd-review-reject-${r.request_id}`}
              >
                <X style={{ width: 9, height: 9, display: 'inline', marginRight: 2 }} />Reject
              </button>
              <button
                onClick={() => decide(r.request_id, 'approve')}
                disabled={actingOn === r.request_id}
                style={{ fontSize: 9, fontWeight: 600, padding: '3px 10px', borderRadius: 5, background: C.black, color: '#fff', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                data-testid={`fd-review-approve-${r.request_id}`}
              >
                <Check style={{ width: 9, height: 9, display: 'inline', marginRight: 2 }} />Approve
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FDBookingReview;
